import React, { useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  notification,
  Select,
  ColorPicker,
  Alert,
} from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { updateProject, getProjectDetails } from "../services/projectsAPI";
import queryClient from "../queryClient";
import { useAuth } from "../hooks/useAuth";
import { fetchCurrentUser } from "../services/authAPI";
import AttachmentsSection from "./AttachmentsSection";
import ProjectAddressFormFields from "./ProjectAddressFormFields";
import ProjectSettingToggle from "./ProjectSettingToggle";
import {
  normalizeBooleanValue,
  parseReminderFormValues,
} from "../utils/projectUtils";
import {
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";

const UpdateProjectModal = ({ visible, onClose, project, readOnly = false }) => {
  const [form] = Form.useForm();
  const { setCurrentUser, setSelectedProject, selectedProject } = useAuth();

  const { data: projectDetails, isFetching: isProjectDetailsFetching } =
    useQuery({
      queryKey: ["projectDetails", project?.project_id],
      queryFn: () => getProjectDetails(project.project_id),
      enabled: visible && !!project?.project_id,
      // Always refetch on open so we never lock the form onto a stale cache hit.
      staleTime: 0,
      refetchOnMount: "always",
    });

  const updateProjectMutation = useMutation({
    mutationFn: ({ projectId, values }) => updateProject(projectId, values),
    onSuccess: async (_data, variables) => {
      // Invalidate currentUser query to refetch updated project data
      const updatedUser = await fetchCurrentUser();
      setCurrentUser(updatedUser);
      setSelectedProject(
        updatedUser.projects.find(
          (p) => p.project_id === selectedProject?.project_id
        )
      );
      localStorage.setItem("user", JSON.stringify(updatedUser));
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({
        queryKey: ["projectDetails", variables.projectId],
      });
      notification.success({ message: "Project updated successfully" });
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update project",
        description: error.message,
      });
    },
  });

  const hydratedKeyRef = useRef(null);
  const hydratedProjectIdRef = useRef(null);
  const hydratedValuesRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      hydratedKeyRef.current = null;
      hydratedProjectIdRef.current = null;
      hydratedValuesRef.current = null;
      return;
    }

    const projectKey = String(project?.project_id || "");
    if (hydratedProjectIdRef.current !== projectKey) {
      // Form instances survive Modal close/open cycles. Clear values and touched
      // state before hydrating another project so fields such as company_logo
      // cannot leak from the previously opened project.
      form.resetFields();
      hydratedKeyRef.current = null;
      hydratedProjectIdRef.current = projectKey;
    }

    // While details are refetching, ignore cached projectDetails — otherwise a
    // stale cache hit hydrates + latches as "details" and the fresh response
    // cannot update the form (reverting fields on the next save).
    const detailsReady = !!projectDetails && !isProjectDetailsFetching;
    const source = detailsReady ? projectDetails : project;
    if (!source) return;

    const hydrateKey = `${projectKey}:${detailsReady ? "details" : "list"}`;
    // Hydrate once on open; allow a single upgrade when fresh projectDetails arrives.
    // Never re-apply server values on parent re-renders (that wiped logos / edits).
    if (hydratedKeyRef.current === hydrateKey) return;
    if (
      hydratedKeyRef.current &&
      hydratedKeyRef.current.startsWith(`${projectKey}:details`)
    ) {
      return;
    }

    const getSetting = (key, camelKey) =>
      source?.[key] ??
      source?.[camelKey] ??
      source?.settings?.[key] ??
      source?.settings?.[camelKey];

    const restrictVendorVisibility = normalizeBooleanValue(
      getSetting("restrict_vendor_visibility", "restrictVendorVisibility")
    );

    const openSubmittalsSummaryEnabled = normalizeBooleanValue(
      getSetting(
        "open_submittals_summary_enabled",
        "openSubmittalsSummaryEnabled"
      )
    );

    const trackMaterialRelease = normalizeBooleanValue(
      getSetting("track_material_release", "trackMaterialRelease")
    );

    const includeStampPageOnCoversheet = normalizeBooleanValue(
      getSetting(
        "include_stamp_page_on_coversheet",
        "includeStampPageOnCoversheet"
      )
    );

    const notifyReceivedFromOnSubmittalClosed = normalizeBooleanValue(
      getSetting(
        "notify_received_from_on_submittal_closed",
        "notifyReceivedFromOnSubmittalClosed"
      )
    );

    const submittalNotifyBallInCourtOnly = normalizeBooleanValue(
      getSetting(
        "submittal_notify_ball_in_court_only",
        "submittalNotifyBallInCourtOnly"
      )
    );

    const debounceNotificationEmails = normalizeBooleanValue(
      getSetting(
        "debounce_notification_emails",
        "debounceNotificationEmails"
      ) ??
        getSetting(
          "rfi_debounce_notification_emails",
          "rfiDebounceNotificationEmails"
        )
    );

    const disableAllEmailNotifications = normalizeBooleanValue(
      getSetting(
        "disable_all_email_notifications",
        "disableAllEmailNotifications"
      )
    );

    const doNotSendReminderEmailsOnWeekends = normalizeBooleanValue(
      getSetting(
        "do_not_send_reminder_emails_on_weekends",
        "doNotSendReminderEmailsOnWeekends"
      )
    );

    const timeChangeOrders = normalizeBooleanValue(
      getSetting("time_change_orders", "timeChangeOrders")
    );

    const dailyReportPdfFullWidthImages = normalizeBooleanValue(
      getSetting(
        "daily_report_pdf_full_width_images",
        "dailyReportPdfFullWidthImages"
      )
    );

    const subcontractorsCanCreateSubmittalsRfis = normalizeBooleanValue(
      getSetting(
        "subcontractors_can_create_submittals_rfis",
        "subcontractorsCanCreateSubmittalsRfis"
      )
    );

    const currentLogo = form.getFieldValue("company_logo");
    const preserveLogo =
      form.isFieldTouched("company_logo") ||
      (Array.isArray(currentLogo) &&
        currentLogo.length > 0 &&
        JSON.stringify(currentLogo) !==
          JSON.stringify(source.company_logo || []));

    form.setFieldsValue({
      name: source.name,
      description: source.description,
      legal_company_name: source.legal_company_name,
      legal_company_address: source.legal_company_address,
      legal_company_phone: formatPhoneNumber(source.legal_company_phone),
      rfi_workflow_type: source.rfi_workflow_type || "Parallel",
      rfi_sequential_requires_all: source.rfi_sequential_requires_all || false,
      restrict_vendor_visibility: restrictVendorVisibility,
      open_submittals_summary_enabled: openSubmittalsSummaryEnabled,
      track_material_release: trackMaterialRelease,
      include_stamp_page_on_coversheet: includeStampPageOnCoversheet,
      settings: {
        notify_received_from_on_submittal_closed:
          notifyReceivedFromOnSubmittalClosed,
        submittal_notify_ball_in_court_only: submittalNotifyBallInCourtOnly,
        debounce_notification_emails: debounceNotificationEmails,
        disable_all_email_notifications: disableAllEmailNotifications,
        do_not_send_reminder_emails_on_weekends:
          doNotSendReminderEmailsOnWeekends,
        time_change_orders: timeChangeOrders,
        daily_report_pdf_full_width_images: dailyReportPdfFullWidthImages,
        subcontractors_can_create_submittals_rfis:
          subcontractorsCanCreateSubmittalsRfis,
      },
      company_logo: preserveLogo ? currentLogo : source.company_logo || null,
      table_header_color: source.table_header_color || "rgb(16, 24, 65)",
      use_custom_terminology: source.use_custom_terminology || false,
      country_code: source.country_code ?? "usa",
      street_address: source.street_address ?? "",
      city: source.city ?? "",
      state: source.state ?? "",
      postal_code: source.postal_code ?? "",
    });
    hydratedKeyRef.current = hydrateKey;
    hydratedValuesRef.current = JSON.stringify(form.getFieldsValue());
  }, [
    visible,
    project?.project_id,
    projectDetails,
    isProjectDetailsFetching,
    form,
    project,
  ]);

  const getReminderPayload = () => {
    const parsed = parseReminderFormValues(
      (projectDetails || project)?.reminders
    );
    return parsed.length > 0 ? { reminders: parsed } : {};
  };

  const handleClose = () => {
    const hasUnsavedChanges =
      !readOnly &&
      hydratedValuesRef.current !== null &&
      JSON.stringify(form.getFieldsValue()) !== hydratedValuesRef.current;
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, Close",
        okType: "danger",
        cancelText: "Cancel",
        onOk: onClose,
      });
    } else {
      onClose();
    }
  };

  return (
    <Modal
      title={readOnly ? "Project Settings" : "Update Project"}
      visible={visible}
      onCancel={handleClose}
      footer={null}
    >
      {readOnly && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Only the Project Admin can change project settings."
        />
      )}
      <Form
        form={form}
        disabled={readOnly}
        onFinish={(values) =>
          updateProjectMutation.mutate({
            projectId: project.project_id,
            values: {
              ...values,
              ...getReminderPayload(),
            },
          })
        }
        layout="vertical"
      >
        <Form.Item
          label="Project Name"
          name="name"
          rules={[
            { required: true, message: "Please input the project name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input />
        </Form.Item>
        <Form.Item
          label="Legal Company Name"
          name="legal_company_name"
          rules={[
            { required: true, message: "Please input the legal company name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Company Address" name="legal_company_address">
          <Input />
        </Form.Item>
        <Form.Item label="Company Phone" name="legal_company_phone">
          <Input
            placeholder="e.g. (123) 456-7890"
            onKeyDown={handlePhoneNumberKeyDown}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              form.setFieldsValue({ legal_company_phone: formatted });
            }}
          />
        </Form.Item>

        <ProjectAddressFormFields form={form} />

        {/* Hidden: not shown in UI; kept so updates do not drop the project flag. */}
        <Form.Item name="use_custom_terminology" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item label="RFI Workflow Type" name="rfi_workflow_type">
          <Select>
            <Select.Option value="Parallel">Parallel</Select.Option>
            <Select.Option value="Sequential">Sequential</Select.Option>
          </Select>
        </Form.Item>

        <div className="project-settings-grid">
          <ProjectSettingToggle
            name="rfi_sequential_requires_all"
            title="RFI Sequential Requires All"
            description="Applicable for sequential RFI review."
          />
          <ProjectSettingToggle
            name="restrict_vendor_visibility"
            title="Restrict Vendor Visibility"
            description="Subcontractors and consultants only see items assigned to them."
          />
          <ProjectSettingToggle
            name="open_submittals_summary_enabled"
            title="Weekly Open Submittal Summary"
            description="Email a PDF report to project owners and managers every Friday."
          />
          <ProjectSettingToggle
            name="track_material_release"
            title="Track Material Release"
            description="Show released-on and estimated-delivery dates on submittals."
          />
          <ProjectSettingToggle
            name="include_stamp_page_on_coversheet"
            title="Include Stamp Page on Coversheet"
            description="Add a second coversheet page with a consultant comments box."
          />
          <ProjectSettingToggle
            name={["settings", "notify_received_from_on_submittal_closed"]}
            title="Notify Received From on Submittal/RFI Closed"
            description="Email the subcontractor when a submittal or RFI changes from Open to Closed."
          />
          <ProjectSettingToggle
            name={["settings", "submittal_notify_ball_in_court_only"]}
            title="Submittal Email: Ball in Court Only"
            description="Email only the person taking action and current ball-in-court users."
          />
          <ProjectSettingToggle
            name={["settings", "debounce_notification_emails"]}
            title="Delay Notification Emails (10 min)"
            description="Wait 10 minutes before sending RFI and submittal create/update emails so you can fix mistakes. Further edits reset the timer; the email uses the latest state."
          />
          <ProjectSettingToggle
            name={["settings", "disable_all_email_notifications"]}
            title="Turn Off All Email Notifications"
            description={
              <>
                When this setting is on, individual email notification switches
                will not work even if they are kept on. Only login codes,
                drawing upload notifications, and custom report emails will
                still be sent.
              </>
            }
          />
          <ProjectSettingToggle
            name={["settings", "do_not_send_reminder_emails_on_weekends"]}
            title="Do Not Send Reminder Emails on Weekends"
            description="Skip RFI and submittal reminder emails on Saturday and Sunday"
          />
          <ProjectSettingToggle
            name={["settings", "time_change_orders"]}
            title="Time Change Orders"
            description="Track contract duration and extra days on change orders (no-charge time extensions)."
          />
          <ProjectSettingToggle
            name={["settings", "daily_report_pdf_full_width_images"]}
            title="Full-Width Daily Report Images"
            description="Show larger, higher-resolution images in a single column, with up to two images per page. When off, images are shown side by side."
          />
          <ProjectSettingToggle
            name={["settings", "subcontractors_can_create_submittals_rfis"]}
            title="Subcontractors Can Create Submittals and RFIs"
            description="Let subcontractors create submittals and RFIs. They still cannot edit or delete them."
          />
        </div>

        <Form.Item
          name="company_logo"
          label="Company Logo"
          className="w-full mt-4"
          valuePropName="value"
          getValueFromEvent={(value) => value}
        >
          <AttachmentsSection
            projectId={project?.project_id}
            disabled={readOnly}
            multiple={false}
            accept="image/*"
            label="Add Company Logo"
            title="Upload Company Logo"
            description="Upload your company logo here. Only image files are accepted."
          />
        </Form.Item>

        <Form.Item name="table_header_color" label="Project Theme Color">
          <ColorPicker
            format="rgb"
            showText
            defaultValue="rgb(16, 24, 65)"
            onChange={(color) => {
              const rgb = color.toRgb();
              form.setFieldValue(
                "table_header_color",
                `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
              );
            }}
          />
        </Form.Item>

        {!readOnly && (
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateProjectMutation.isPending}
            >
              Update
            </Button>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default UpdateProjectModal;
